import { observer } from "mobx-react-lite";
import type React from "react";
import type { PropsWithChildren } from "react";
import type { ViewStyle } from "react-native";
import { View, XStack, type XStackProps, YStack } from "tamagui";
import { PrimaryCheckText } from "../core/components/PrimaryCheck";
import type BaseModuleModel from "../modules/core/BaseModuleModel";
import { Body } from "./Body";
import { InfoRow, type InfoRowItemProps } from "./InfoRow";
import { Label, LabelSkeleton } from "./Label";
import { LoadingCircular } from "./LoadingCircular";
import { ModuleIcon } from "./ModuleIcon";
import { Skeleton } from "./Skeleton";
import { SubtextSkeleton } from "./Subtext";

export type BlockProps = {
  icon?: JSX.Element;
  infoRowItems?: InfoRowItemProps[];
  first?: boolean;
  last?: boolean;
  style?: ViewStyle;
  space?: number | string;
  grouped?: boolean;
  active?: boolean;
} & XStackProps;

export const Block: React.FC<PropsWithChildren<BlockProps>> = ({
  children,
  infoRowItems,
  icon,
  first,
  last,
  grouped,
  style,
  space,
  active,
  onPress,
  ...xStackProps
}) => {
  return (
    <XStack
      width="100%"
      style={style}
      borderColor={"$divider"}
      borderTopWidth={first || grouped ? 0 : 1}
      gap={"$medium"}
      backgroundColor={active ? "$background" : undefined}
      onPress={onPress}
      cursor={onPress ? "pointer" : undefined}
      {...xStackProps}
    >
      {icon ? (
        <View alignItems="center" width={"$size.medium"}>
          {icon}
        </View>
      ) : undefined}
      <YStack alignItems="flex-start" gap="$medium" flex={1}>
        {children}
        {!!infoRowItems?.length && <InfoRow items={infoRowItems} />}
      </YStack>
    </XStack>
  );
};

export const ModuleBlock: React.FC<
  PropsWithChildren<
    {
      // biome-ignore lint/suspicious/noExplicitAny: Because
      module: BaseModuleModel<any, any>;
      infoRowItems?: InfoRowItemProps[];
      doneText?: string;
    } & BlockProps
  >
> = observer(({ module, infoRowItems, doneText, children, ...blockProps }) => {
  return (
    <Block
      grouped
      infoRowItems={module.isDone ? undefined : infoRowItems}
      icon={
        <View position="relative">
          <ModuleIcon key={`${module.data.emoji}`} module={module} />
        </View>
      }
      {...blockProps}
    >
      <Label alignSelf="flex-start">
        {module.data.title ? module.data.title : "Set title..."}
      </Label>
      {module.isDone && doneText ? (
        <PrimaryCheckText text={doneText} />
      ) : undefined}
      {children}
    </Block>
  );
});

export const ModuleBlockSkeleton = () => {
  return (
    <Block grouped icon={<Skeleton width={24} height={24} />}>
      <LabelSkeleton />
      <SubtextSkeleton />
    </Block>
  );
};

export const BlockLoading = ({
  children,
  ...blockProps
}: PropsWithChildren<BlockProps>) => {
  return (
    <Block
      icon={<LoadingCircular />}
      paddingHorizontal="$medium"
      {...blockProps}
    >
      <Label>Loading...</Label>
      <Body>Don't worry, we're loading your content. It's soon ready!</Body>
      {children}
    </Block>
  );
};
